// src/hooks/useEmbedTheme.ts
import { useEffect, useState } from 'react';

// Theme for the embeddable/standalone surfaces (Writing Dashboard, World Builder,
// Storyboard). Resolution order:
//   1. Explicit `?theme=dark|light` — the desktop client can pass this on the
//      embed URL for guaranteed consistency with its own light/dark mode.
//   2. Otherwise `prefers-color-scheme` — zero-config: the WebView (and browsers)
//      report the OS/client theme, and this tracks it live.
// Scoped to these surfaces on purpose; the marketing site stays light.
export function useEmbedTheme(): 'dark' | 'light' {
  const param = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('theme')
    : null;

  const prefersDark = () =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const resolve = (): 'dark' | 'light' => {
    if (param === 'dark') return 'dark';
    if (param === 'light') return 'light';
    return prefersDark() ? 'dark' : 'light';
  };

  const [theme, setTheme] = useState<'dark' | 'light'>(resolve);

  useEffect(() => {
    if (param === 'dark' || param === 'light') { setTheme(param); return; } // explicit wins
    if (!window.matchMedia) { setTheme('light'); return; }                  // no support → light
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setTheme(mq.matches ? 'dark' : 'light');
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [param]);

  return theme;
}
